import type {
    ResolverActionDto,
    ResolverActionType,
    ResolverCodeType,
    ResolverGraphAction,
} from "../../../../../types/resolver";

const getDerivedAttributeType = (value: string | number | boolean): "BOOLEAN" | "NUMBER" | "STRING" => {
    if (typeof value === "boolean") return "BOOLEAN";
    if (typeof value === "number") return "NUMBER";
    return "STRING";
};

const makeActionId = (): string => `action-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Normalize graph action's actionDetails into backend-ready shape. */
export function graphActionToDtoDetails(graphAction: ResolverGraphAction): Record<string, unknown> {
    const d = graphAction.actionDetails ?? {};
    switch (graphAction.action) {
        case "SET_DERIVED_ATTRIBUTE": {
            const value = d.derivedAttributeValue;
            return {
                derivedAttributeName: d.derivedAttributeName ?? "RESULT",
                derivedAttributeValue: value,
                derivedAttributeType:
                    d.derivedAttributeType ?? (value != null ? getDerivedAttributeType(value as string | number | boolean) : "STRING"),
            };
        }
        case "SET_ANSWER":
            return {
                questionId: d.questionId != null ? String(d.questionId) : "",
                answerSetId: d.answerSetId != null ? String(d.answerSetId) : undefined,
                answerId: d.answerId != null ? String(d.answerId) : undefined,
            };
        case "ADD_CODE": {
            const attrs = (d.attributes as Array<{ answerSetId?: unknown; answerId?: unknown; answerValueId?: unknown }>) ?? [];
            const attributes = attrs
                .filter((a) => a != null)
                .map((a) => ({
                    answerSetId: a.answerSetId != null ? String(a.answerSetId) : "",
                    answerId: (a.answerId ?? a.answerValueId) != null ? String(a.answerId ?? a.answerValueId) : "",
                }));
            const rawDerived = d.derivedAttributes as Record<string, unknown> | undefined;
            const derivedAttributes =
                rawDerived != null && typeof rawDerived === "object" && !Array.isArray(rawDerived)
                    ? rawDerived
                    : {};
            return {
                code: d.code != null ? String(d.code) : "",
                codeType: (d.codeType as ResolverCodeType) ?? "DIAGNOSIS",
                mark_code_as_ideal: Boolean(d.mark_code_as_ideal),
                attributes: attributes.length > 0 ? attributes : undefined,
                derivedAttributes: Object.keys(derivedAttributes).length > 0 ? derivedAttributes : undefined,
            };
        }
        case "THROW_ERROR":
            return {
                message: d.message != null ? String(d.message) : "Resolver validation failed",
                errorCode: d.errorCode != null ? String(d.errorCode) : undefined,
                severity: d.severity != null ? String(d.severity) : undefined,
                field: d.field != null ? String(d.field) : undefined,
                stopExecution: typeof d.stopExecution === "boolean" ? d.stopExecution : true,
                nodeId: typeof d.nodeId === "number" ? d.nodeId : undefined,
                details: typeof d.details === "object" && d.details !== null ? (d.details as Record<string, unknown>) : undefined,
            };
        case "MARK_CODE_AS_IDEAL":
            return {};
        default:
            return { ...d };
    }
}

/** Default action details per type for new actions in the UI. */
export function getDefaultActionDetails(actionType: ResolverActionType): Record<string, unknown> {
    switch (actionType) {
        case "SET_DERIVED_ATTRIBUTE":
            return {
                derivedAttributeName: "RESULT",
                derivedAttributeValue: true,
                derivedAttributeType: "BOOLEAN",
            };
        case "SET_ANSWER":
            return { questionId: "", answerSetId: "", answerId: "" };
        case "ADD_CODE":
            return { code: "", codeType: "DIAGNOSIS", mark_code_as_ideal: false, attributes: [], derivedAttributes: {} };
        case "THROW_ERROR":
            return {
                message: "Resolver validation failed",
                errorCode: "",
                severity: "ERROR",
                field: "",
                stopExecution: true,
            };
        case "MARK_CODE_AS_IDEAL":
            return {};
        default:
            return {};
    }
}

/** Create a new graph action with default details for the given type. */
export function createDefaultGraphAction(actionType: ResolverActionType): ResolverGraphAction {
    return {
        id: makeActionId(),
        action: actionType,
        actionDetails: getDefaultActionDetails(actionType),
    };
}
